import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

function folderKey(name) {
    return encodeURIComponent(name || "General");
}

function groupFolders(rows) {
    const folders = new Map();
    (rows || []).forEach((row) => {
        const name = String(row.course_name || "General").trim() || "General";
        const current = folders.get(name) || { name, description: "", image: "", count: 0 };
        current.count += 1;
        if (!current.description && row.course_description) current.description = row.course_description;
        if (!current.image && row.course_image) current.image = row.course_image;
        folders.set(name, current);
    });
    return Array.from(folders.values());
}

export default function WebsiteBrochures() {
    const { requestAPI, showToast } = useAppContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/brochures",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load course folders");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const folders = groupFolders(rows);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Course folders" />
            <TabHeader
                className="px-3 pt-3"
                title="Course folders"
                highlights={["A folder is one course on the e-Brochures page, such as CA Foundation", "Open a folder to add the brochure files inside it"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {folders.length ? folders.map((folder) => (
                        <Row key={folder.name} folder={folder} rows={rows} setRows={setRows} requestAPI={requestAPI} showToast={showToast} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <AddFolder setDialog={setDialog} folders={folders} navigate={navigate} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ folder, rows, setRows, requestAPI, showToast }) {
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState();
    const remove = () => {
        const matches = (rows || []).filter((row) => (String(row.course_name || "General").trim() || "General") === folder.name);
        if (!matches.length) return;
        setDeleting(true);
        let pending = matches.length;
        matches.forEach((row) => {
            requestAPI({
                requestPath: `website/brochures/${row.id}`,
                requestMethod: "DELETE",
                parseResponseBody: false,
                onResponseReceieved: (_, responseCode) => {
                    if (responseCode === 204) setRows((prev) => prev?.filter((item) => item.id !== row.id));
                    pending -= 1;
                    if (pending === 0) {
                        setDeleting(false);
                        showToast({ severity: "success", summary: "Deleted", detail: "Course folder removed", life: 1000 });
                    }
                },
                onRequestFailure: () => {
                    pending -= 1;
                    if (pending === 0) setDeleting(false);
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                },
            });
        });
    };
    return (
        <div className="flex gap-3 align-items-center border-round py-2 px-3 text-white bg-green-500">
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{folder.name}</span>
                <span className={TEXT_SMALL}>{folder.count} brochure{folder.count === 1 ? "" : "s"}</span>
            </div>
            <i className="pi pi-folder-open cursor-pointer" title="Open folder" onClick={() => navigate(`/manage-website/brochures/folders/${folderKey(folder.name)}`)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" title="Remove this folder and its brochures" /></ConfirmationWrapper>} />
        </div>
    );
}

function AddFolder({ setDialog, folders, navigate, showToast }) {
    const [record, setRecord] = useState({ name: "", description: "", image: "" });
    const open = () => {
        const name = String(record.name || "").trim();
        if (!name) {
            showToast({ severity: "error", summary: "Name required", detail: "Enter the course folder name", life: 2000 });
            return;
        }
        const existing = folders.find((folder) => folder.name.toLowerCase() === name.toLowerCase());
        navigate(`/manage-website/brochures/folders/${folderKey(existing?.name || name)}`, {
            state: { course_description: record.description, course_image: record.image },
        });
    };
    return (
        <Dialog header="Add a course folder" visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="name" className="w-full" value={record.name} onChange={(event) => setRecord((prev) => ({ ...prev, name: event.target.value }))} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="name">Course folder name</label>
                    </FloatLabel>
                    <FieldNote text="Shown on the e-Brochures page. Example: CA Foundation or 11th Commerce." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="description" className="w-full" rows={3} autoResize value={record.description} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} />
                        <label htmlFor="description">Line under the folder name</label>
                    </FloatLabel>
                </div>
                <WebsiteMediaInput label="Folder picture" field="image" hint="Required size: 256 ? 256 px (square). A larger picture is scaled to fit this small square. The whole picture stays visible." record={record} setRecord={setRecord} />
                <FieldNote text="The folder appears on the website after you add at least one brochure file." />
                <Button className="mt-5 align-self-start" label="Open folder" icon="pi pi-arrow-right" iconPos="right" onClick={open} />
            </div>
        </Dialog>
    );
}
